import { Dayjs } from "dayjs";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT, YEAR_MONTH_STR_FORMAT } from "@/constants/datetime";
import { findLabelByValue } from "@/utils/object";
import { TOption } from "@/types/common";

// Display types supported by the 現在の値 helper text.
// Mirrors the input families used inside the scan modal forms.
export type CurrentValueType = "text" | "date" | "yearMonth" | "select" | "selectMulti" | "multiline";

// Keys for option lists registered in CurrentTalentProvider.
// Used to look up label-from-value for select / selectMulti fields.
export type CurrentOptionsKey =
  | "nationality"
  | "gender"
  | "certificate"
  | "major"
  | "languageTestLevel"
  | "jobType"
  | "languageTest"
  | "location"
  | "recruitmentStatus"
  | "residenceStatus";

const EMPTY = "-";

const isEmpty = (v: unknown): boolean => {
  if (v == null) return true;
  if (typeof v === "string" && v.trim() === "") return true;
  if (Array.isArray(v) && v.filter((x) => x != null && x !== "").length === 0) return true;
  return false;
};

// Format a raw value pulled from the talent object into a user-facing string.
// Returns "-" for empty / null / undefined / empty array.
export const formatCurrentValue = (rawValue: unknown, type: CurrentValueType = "text", options?: TOption[]): string => {
  if (isEmpty(rawValue)) return EMPTY;

  switch (type) {
    case "date":
      return dayjs(rawValue as Dayjs | string).isValid() ? dayjs(rawValue as Dayjs | string).format(DATE_FORMAT) : EMPTY;

    case "yearMonth":
      return dayjs(rawValue as Dayjs | string).isValid() ? dayjs(rawValue as Dayjs | string).format(YEAR_MONTH_STR_FORMAT) : EMPTY;

    case "select": {
      const label = findLabelByValue(options ?? [], rawValue as string | number);
      return label || EMPTY;
    }

    case "selectMulti": {
      const arr = (rawValue as (string | number | null | undefined)[]).filter((v) => v != null && v !== "");
      const labels = arr.map((v) => findLabelByValue(options ?? [], v as string | number)).filter(Boolean);
      return labels.length > 0 ? labels.join("、") : EMPTY;
    }

    case "multiline":
    case "text":
    default:
      return String(rawValue);
  }
};

// Build the final helper text shown under a scan-modal input.
export const buildCurrentValueText = (rawValue: unknown, type: CurrentValueType = "text", options?: TOption[]): string => {
  return `現在の値：${formatCurrentValue(rawValue, type, options)}`;
};
